import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import {
  validateName,
  validateEmail,
  sanitizeInput,
  sanitizeInputLight,
} from "../../lib/sanitizer";

export default function RegisterForm() {
  const navigate = useNavigate();
  const { register, loading } = useAuthStore();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Sử dụng sanitize nhẹ cho trường name khi đang gõ
    let sanitizedValue = value;
    if (name === "name") {
      sanitizedValue = sanitizeInputLight(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate name with sanitization
    const nameValidation = validateName(formData.name);
    if (!nameValidation.isValid) {
      newErrors.name = nameValidation.error;
    }

    // Validate email with sanitization
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }

    // Validate password
    if (!formData.password.trim()) {
      newErrors.password = "Mật khẩu là bắt buộc";
    } else if (formData.password.length < 6) {
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    // Validate confirm password
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = "Xác nhận mật khẩu là bắt buộc";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Mật khẩu không khớp";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      // Sanitize all form data before sending
      const sanitizedData = {
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        password: formData.password, // Don't sanitize password
      };

      const result = await register(sanitizedData);

      if (result.success) {
        navigate("/");
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      setErrors({ submit: "Đã xảy ra lỗi không mong muốn" });
    }
  };

  return (
    <div className="auth-form">
      <h2 className="auth-form__title">Đăng ký</h2>

      <form onSubmit={handleSubmit} className="auth-form__form">
        <div className="auth-form__field">
          <label htmlFor="name" className="auth-form__label">
            Tên
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`auth-form__input ${
              errors.name ? "auth-form__input--error" : ""
            }`}
            placeholder="Nhập tên bạn muốn hiển thị"
            autoComplete="name"
          />
          {errors.name && (
            <span className="auth-form__error">{errors.name}</span>
          )}
          <small className="auth-form__help">
            Tên chỉ được chứa chữ cái, số và khoảng trắng
          </small>
        </div>

        <div className="auth-form__field">
          <label htmlFor="email" className="auth-form__label">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`auth-form__input ${
              errors.email ? "auth-form__input--error" : ""
            }`}
            placeholder="Nhập email của bạn"
            autoComplete="email"
          />
          {errors.email && (
            <span className="auth-form__error">{errors.email}</span>
          )}
        </div>

        <div className="auth-form__field">
          <label htmlFor="password" className="auth-form__label">
            Mật khẩu
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={`auth-form__input ${
              errors.password ? "auth-form__input--error" : ""
            }`}
            placeholder="Nhập mật khẩu"
          />
          {errors.password && (
            <span className="auth-form__error">{errors.password}</span>
          )}
        </div>

        <div className="auth-form__field">
          <label htmlFor="confirmPassword" className="auth-form__label">
            Xác nhận mật khẩu
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={`auth-form__input ${
              errors.confirmPassword ? "auth-form__input--error" : ""
            }`}
            placeholder="Nhập lại mật khẩu"
          />
          {errors.confirmPassword && (
            <span className="auth-form__error">{errors.confirmPassword}</span>
          )}
        </div>

        {errors.submit && (
          <div className="auth-form__submit-error">{errors.submit}</div>
        )}

        <button type="submit" disabled={loading} className="auth-form__submit">
          {loading ? "Đang đăng ký..." : "Đăng ký"}
        </button>
      </form>

      <div className="auth-form__footer">
        <p>
          Đã có tài khoản? <Link to="/login">Đăng nhập ngay</Link>
        </p>
      </div>
    </div>
  );
}
